import { useEffect, useRef, useState } from 'react'
import type { SiteContact } from '../api/types'

/**
 * Kontaktoplysninger man skal kunne trykke på, når man står med mobilen i hånden:
 * nummeret ringer op, adressen åbner et kort (docs/DECISIONS.md beslutning 025).
 *
 * Adressen giver et valg mellem Apple Kort og Google Maps frem for at antage,
 * hvad folk bruger - en blandet flåde af iPhones og Androider er reglen, ikke
 * undtagelsen. Den mest sandsynlige mulighed står først, men begge er der altid,
 * så man ikke skal lede efter den anden.
 */

/** Adressen på én linje: "Vestergade 12, 8000 Aarhus". */
export function formatAddress(contact?: SiteContact | null): string | null {
  if (!contact) return null

  const town = [contact.postalCode, contact.city].filter(Boolean).join(' ')
  const parts = [contact.address, town].map((p) => p?.trim()).filter(Boolean)

  return parts.length === 0 ? null : parts.join(', ')
}

/** Ser det ud til, at vi kører på Apples platform? Bruges kun til rækkefølgen. */
function prefersAppleMaps() {
  const ua = navigator.userAgent
  return /iPhone|iPad|iPod|Macintosh/.test(ua)
}

function mapLinks(address: string) {
  const query = encodeURIComponent(address)

  const apple = {
    key: 'apple',
    label: 'Apple Kort',
    // maps.apple.com åbner Kort-appen på Apple-enheder og et kort i browseren andre steder.
    href: `https://maps.apple.com/?q=${query}`
  }

  const google = {
    key: 'google',
    label: 'Google Maps',
    // api=1 er den officielle, platformsuafhængige form; åbner appen hvor den findes.
    href: `https://www.google.com/maps/search/?api=1&query=${query}`
  }

  return prefersAppleMaps() ? [apple, google] : [google, apple]
}

/**
 * Adressen som en knap. Et tryk åbner valget mellem de to kort-apps.
 * Uden adresse vises en bindestreg, så feltet ikke bare forsvinder.
 */
export function AddressLink({ contact }: { contact?: SiteContact | null }) {
  const address = formatAddress(contact)
  const [open, setOpen] = useState(false)
  const wrapper = useRef<HTMLSpanElement>(null)

  // Et tryk uden for menuen lukker den - ellers bliver den hængende på mobilen.
  useEffect(() => {
    if (!open) return

    function onPointerDown(event: PointerEvent) {
      if (!wrapper.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (!address) {
    return <span className="muted">-</span>
  }

  return (
    <span className="map-pick" ref={wrapper}>
      <button
        type="button"
        className="link-button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        {address}
      </button>

      {open && (
        <span className="map-pick__menu" role="menu">
          <span className="map-pick__title">Åbn adressen i</span>
          {mapLinks(address).map((option) => (
            <a
              key={option.key}
              role="menuitem"
              className="map-pick__option"
              href={option.href}
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpen(false)}
            >
              {option.label}
            </a>
          ))}
        </span>
      )}
    </span>
  )
}

/** Telefonnummeret som et opkald. Mellemrum fjernes, så tel: virker overalt. */
export function PhoneLink({ phone }: { phone?: string | null }) {
  if (!phone?.trim()) {
    return <span className="muted">-</span>
  }

  // tel: tåler ikke mellemrum; det viste nummer beholder dem, så det kan læses.
  const dialable = phone.replace(/[^\d+]/g, '')

  return (
    <a className="phone-link" href={`tel:${dialable}`}>
      {phone}
    </a>
  )
}

/**
 * Hele kontaktblokken: kunde, kontaktperson, telefon og adresse.
 * Bruges der, hvor man er på vej ud - dagens kalender, sagen og enheden.
 */
export function SiteContactFields({ contact }: { contact?: SiteContact | null }) {
  if (!contact) {
    return null
  }

  return (
    <>
      {contact.contactPerson && (
        <div>
          <div className="tiny muted">Kontaktperson</div>
          <div className="small">{contact.contactPerson}</div>
        </div>
      )}
      <div>
        <div className="tiny muted">Telefon</div>
        <div className="small">
          <PhoneLink phone={contact.phone} />
        </div>
      </div>
      <div>
        <div className="tiny muted">Adresse</div>
        <div className="small">
          <AddressLink contact={contact} />
        </div>
      </div>
    </>
  )
}
